import React, { Component } from 'react';
import PropTypes from 'prop-types';
import SSTable from '../SSTable/SSTable';
import SSColumn from '../SSTable/SSColumn';
import ToolTipIcon from '../ToolTipIcon/ToolTipIcon';

/**
 * @typedef {import('../../containers/Pages/TagManager/types').TagRow} TagRow
 * @typedef {import('../../containers/Pages/TagManager/types').TagManagerTableProps} TagManagerTableProps
 */

// disabled in phase 1, delete UX behaviour requires more planning
export const DELETE_FEATURE_ENABLED = false;

export default class TagManagerTable extends Component {
  /**
   * Returns the row tag label.
   *
   * @param {TagRow} row - The tag row.
   * @returns {string} The tag label.
   */
  getTag = row => row.tag;

  /**
   * Renders the Visible column header with the hide-archived checkbox.
   *
   * @returns {JSX.Element} The header markup.
   */
  getVisibleHeader = () => (
    <span className="inline-flex items-center gap-2">
      <span>Visible</span>
      <input
        type="checkbox"
        checked={this.props.filterState.hideArchived}
        onChange={this.props.onHideArchivedChange}
        data-test="tagmanager-hide-archived"
        className="w-[18px] h-[18px] cursor-pointer"
      />
    </span>
  );

  /**
   * Renders the visibility checkbox for a row.
   *
   * @param {TagRow} row - The tag row.
   * @returns {JSX.Element} The checkbox markup.
   */
  getVisible = row => {
    const { onArchiveToggle, actionsDisabled } = this.props;

    return (
      <input
        type="checkbox"
        checked={row.archived !== 1}
        onChange={() => onArchiveToggle(row)}
        disabled={actionsDisabled}
        data-test={`tagmanager-visible-${row.id}`}
        className="w-[18px] h-[18px] cursor-pointer"
      />
    );
  };

  /**
   * Filters tag rows by a case-insensitive substring of the tag label.
   *
   * @param {TagRow[]} rows - Tag rows.
   * @param {string} filter - The filter text.
   * @returns {TagRow[]} Rows whose tag contains the filter text.
   */
  filterTagRows = (rows, filter) => {
    const needle = (filter || '').trim().toLowerCase();

    if (!needle) {
      return rows;
    }

    return rows.filter(row => row.tag.toLowerCase().includes(needle));
  };

  /**
   * Renders the Tag column header with the filter input.
   *
   * @returns {JSX.Element} The header markup.
   */
  getTagHeader = () => {
    const { filterState, onFilterChange } = this.props;

    return (
      <span className="inline-flex items-center gap-2">
        <span>Tag</span>
        <input
          type="text"
          value={filterState.filter}
          onChange={e => onFilterChange(e.target.value)}
          placeholder="Filter tags..."
          data-test="tagmanager-filter"
          className="w-[150px] h-[30px] px-2 rounded-lg border border-gray-300 text-sm font-normal text-gray-700"
        />
      </span>
    );
  };

  /**
   * Renders the row action buttons.
   *
   * @param {TagRow} row - The tag row.
   * @returns {JSX.Element} The actions markup.
   */
  getActions = row => {
    const { onRename, onDelete, actionsDisabled } = this.props;

    return (
      <div
        className="w-full bg-transparent h-full flex justify-center items-center gap-2"
        data-test="tagmanagertable-actions"
      >
        <ToolTipIcon toolText="Rename">
          <button
            type="button"
            onClick={() => onRename(row)}
            disabled={actionsDisabled}
            data-test={`tagmanager-rename-${row.id}`}
            className="duration-500 hover:scale-105 w-[30px] cursor-pointer h-[30px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/edit.svg"
              alt="Rename"
              className="w-[20px] h-[20px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
        {DELETE_FEATURE_ENABLED && (
          <ToolTipIcon toolText="Delete">
            <button
              type="button"
              onClick={() => onDelete(row)}
              disabled={actionsDisabled}
              data-test={`tagmanager-delete-${row.id}`}
              className="duration-500 hover:scale-105 w-[30px] cursor-pointer h-[30px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/delete_button.svg"
                alt="Delete"
                className="w-[20px] h-[20px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}
      </div>
    );
  };

  /**
   * Renders the tag table with the tag filter, visibility column, and row
   * actions.
   *
   * @returns {JSX.Element|null} The table markup, or null when no data.
   */
  render() {
    const { data, filterState } = this.props;
    const { filter, hideArchived } = filterState;

    if (!data) {
      return null;
    }

    const filteredData = this.filterTagRows(data, filter).filter(
      row => !hideArchived || row.archived !== 1,
    );

    return (
      <SSTable
        className="responsive table-tagmanager"
        data={filteredData}
        rowHeight={50}
        rowsCount={filteredData.length - 1}
        zebra
      >
        {/* not needed for now
        <SSColumn center width={10} header="Id" cell={this.getId} />
        */}
        <SSColumn header={this.getTagHeader()} cell={this.getTag} />
        <SSColumn center width={15} header={this.getVisibleHeader()} cell={this.getVisible} />
        <SSColumn center width={20} header="Actions" cell={this.getActions} />
      </SSTable>
    );
  }
}

TagManagerTable.propTypes = {
  data: PropTypes.array,
  actionsDisabled: PropTypes.bool,
  onRename: PropTypes.func.isRequired,
  onArchiveToggle: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onHideArchivedChange: PropTypes.func.isRequired,
  filterState: PropTypes.shape({
    filter: PropTypes.string.isRequired,
    hideArchived: PropTypes.bool.isRequired,
  }).isRequired,
};
